/**
 * Taxonomia canônica do nome de quarto — fase 1 do agrupamento de quartos.
 *
 * O fornecedor só manda texto livre; categoria e cama existem SÓ dentro da
 * string. Este módulo é puro (zero IO, zero i18n): emite TOKENS, quem escreve
 * palavras é a view. Léxico calibrado contra 533.761 reservas do legado e
 * verificado no golden fixture de 5 hotéis reais (teste vizinho).
 */

import type {
  BedFamily,
  RoomBed,
  RoomClassification,
  RoomIdentity,
  RoomQualifier,
  RoomQualifierCode,
  RoomTier,
  RoomType,
} from './hotel_room_vocabulary.js';

export type {
  RoomBed,
  RoomClassification,
  RoomIdentity,
  RoomQualifier,
  RoomTier,
} from './hotel_room_vocabulary.js';

const NUMBER_WORDS: Record<string, string> = {
  UMA: '1',
  UM: '1',
  DUAS: '2',
  DOIS: '2',
  TRES: '3',
  QUATRO: '4',
  CINCO: '5',
  ONE: '1',
  TWO: '2',
  THREE: '3',
};

export function normalizeRoomText(raw: string): string {
  const stripped = raw
    .normalize('NFD')
    .replaceAll(/\p{M}/gu, '')
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]+/g, ' ')
    .replaceAll(/\b0(\d)\b/g, '$1')
    .trim()
    .replaceAll(/\s+/g, ' ');
  return stripped
    .split(' ')
    .map((token) => NUMBER_WORDS[token] ?? token)
    .join(' ');
}

/** R1: divide o CRU (a normalização destrói os separadores) e normaliza cada
 *  segmento. Só separador COM espaços (`\s` simples: `\s+` é super-linear, S5852;
 *  o excesso some no trim da normalização) — hífen colado é composição. */
export function segmentRoomName(raw: string): string[] {
  return raw
    .split(/\s[-|/]\s/)
    .map(normalizeRoomText)
    .filter((segment) => segment.length > 0);
}

const PLACEHOLDER_PATTERNS = [
  /^$/,
  /^NULL( NULL)*$/,
  /SERA DEFINID/,
  /A DEFINIR/,
  /COTACAO/,
  /CONFIRMACAO PENDENTE/,
  /^PENDENTE/,
  /REGULARIZACAO/,
  /^N A$/,
];

export function isPlaceholderRoomName(raw: string): boolean {
  const normalized = normalizeRoomText(raw);
  return PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(normalized));
}

type LexEntry<T extends string> = readonly [RegExp, T];

const TYPES: readonly LexEntry<RoomType>[] = [
  [/\bAPARTAMENTOS?\b|\bAPARTMENTS?\b|\bAPTOS?\b/, 'APARTAMENTO'],
  [/\bSUITES?\b/, 'SUITE'],
  [/\bSTUDIOS?\b|\bESTUDIOS?\b/, 'STUDIO'],
  [/\bCHALES?\b|\bCHALET\b/, 'CHALE'],
  [/\bBANGALOS?\b|\bBUNGALOWS?\b/, 'BUNGALOW'],
  [/\bLOFTS?\b/, 'LOFT'],
  [/\bFLATS?\b/, 'FLAT'],
  [/\bCABANAS?\b/, 'CABANA'],
];
/** QUARTO/ROOM consome mas NÃO deposita: todo quarto é um quarto. */
const DEFAULT_TYPE = /\bQUARTOS?\b|\bROOMS?\b|\bHABITACION\w*\b/;

const TIERS: readonly LexEntry<RoomTier>[] = [
  [/\bSTANDARDS?\b|\bSTANDART\b|\bPADRAO\b|\bSTD\b|\bESTANDAR\b/, 'STANDARD'],
  [/\bSUPERIORE?S?\b/, 'SUPERIOR'],
  [/\bDE ?LUXE\b|\bDELUXE\b/, 'DELUXE'],
  [/\bSUPER ?LUXOS?\b|\bLUXOS?\b|\bLUXURY\b/, 'LUXO'],
  [/\bEXECUTIV[OA]S?\b|\bEXECUTIVE\b/, 'EXECUTIVO'],
  [/\bPREMIUM\b/, 'PREMIUM'],
  [/\bMASTER\b/, 'MASTER'],
  [/\bJUNIOR\b|\bJR\b/, 'JUNIOR'],
  [/\bPRIME\b/, 'PRIME'],
  [/\bCLASSIC[OA]?\b/, 'CLASSIC'],
  [/\bCONFORT\w*\b|\bCOMFORT\w*\b/, 'CONFORT'],
  [/\bECONOMIC[OA]\b|\bECONOMY\b/, 'ECONOMICO'],
];

/** R3: o degrau mais alto vence; o perdedor vira tier2 (fundir Prime Junior em
 *  Junior mostraria preço de outro quarto — a falha proibida). */
const TIER_RANK: Record<RoomTier, number> = {
  ECONOMICO: 0,
  STANDARD: 1,
  CLASSIC: 1,
  JUNIOR: 1,
  CONFORT: 2,
  SUPERIOR: 3,
  EXECUTIVO: 4,
  DELUXE: 5,
  LUXO: 5,
  PRIME: 6,
  PREMIUM: 6,
  MASTER: 7,
};

const QUALIFIERS: readonly LexEntry<RoomQualifierCode>[] = [
  [/\bNAO FUMANTES?\b|\bNON ?SMOKING\b|\bN FUMANTE\b/, 'NO_SMOKING'],
  [/\bFUMANTES?\b|\bSMOKING\b/, 'SMOKING'],
  [/\bSEM AR ?CONDICIONADO\b/, 'NO_AC'],
  [/\b(?:COM )?AR ?CONDICIONADO\b|\bAIR ?CON\w*\b/, 'AC'],
  [/\bVISTA (?:PARA )?(?:O |A )?MAR\b|\bSEA VIEW\b|\bOCEAN VIEW\b|\bFRENTE MAR\b/, 'SEA_VIEW'],
  [/\bVISTA (?:PARA )?(?:O |A )?JARDIM\b|\bGARDEN VIEW\b/, 'GARDEN_VIEW'],
  [/\bVISTA (?:PARA )?(?:A )?CIDADE\b|\bCITY VIEW\b/, 'CITY_VIEW'],
  [/\bVISTAS?\b|\bVIEW\b/, 'VIEW'],
  [/\bVARANDAS?\b|\bBALCONY\b|\bTERRACE\b/, 'BALCONY'],
  [/\bPRIVATIV[OA]\b|\bPRIVATE\b/, 'PRIVATE'],
  // Acessibilidade (PCD) — eixo de SEGURANÇA: rotular errado é pior que dividir.
  // `ADAPTAD\w*` (não `ADAPTAD[OA]`) tolera a palavra PARTIDA pelo ruído de OCR do
  // fornecedor: "ADAPTAD O." normaliza para os tokens `ADAPTAD` + `O`, e "ADAPTAD0"
  // (zero no lugar do O) vira `ADAPTAD0` — o prefixo `ADAPTAD` casa os dois sem
  // religar tokens (issue #2411). O leque PT/EN/ES é ancorado em `\b...\b` e não
  // colide com nenhum outro eixo do léxico (verificado contra o golden). Duas
  // entradas (PT, EN/ES) com o mesmo código: os termos não se sobrepõem e
  // `qualifierCodes` é deduplicado na montagem.
  [
    /\bADAPTAD\w*\b|\bPNE\b|\bPCD\b|\bACESSIVE\w*\b|\bCADEIRANTE\w*\b|\bDEFICIENTE\w*\b|\bDEF FISIC\w*\b|\bMOBILIDADE REDUZIDA\b/,
    'ACCESSIBLE',
  ],
  [
    /\bADAPTED\b|\bACCESSIBLE\b|\bACCESIBLE\b|\bHANDICAPPED\b|\bWHEELCHAIR\b|\bDISABLED\b/,
    'ACCESSIBLE',
  ],
  [/\bCOZINHA\b|\bKITCHEN\w*\b/, 'KITCHEN'],
  [/\bFAMILIA\w*\b|\bFAMILY\b/, 'FAMILY'],
];

/** Metadado de tarifa/contrato/campanha — NUNCA identifica o quarto, e é
 *  redundante com campos estruturados que a linha já imprime (café,
 *  cancelamento, negociada). Medido: remover isso é a maior alavanca. */
const NOISE: readonly RegExp[] = [
  /\bNEGOTIATED RATE\b/,
  /\bNEGOCIADAS?\b/,
  /\bNEG\b/,
  /\bNRLA\b/,
  /\bTRF\b/,
  /\bACORDOS?\b/,
  /\bCORP\w*\b/,
  /\bTARIFAS?\b/,
  /\bRATES?\b/,
  /\bFLEXIVE\w*\b/,
  /\bCAFE DA MANHA\b/,
  /\bSEM CAFE\b/,
  /\bCOM CAFE\b/,
  /\bCAFE\b/,
  /\bMANHA\b/,
  /\bINCLUID[OA]S?\b/,
  /\bINCLUS\w+\b/,
  /\bBREAKFAST\b/,
  /\bBB\b/,
  /\bEP\b/,
  /\bRO\b/,
  /\bNAO REEMBOLSAVEL\b/,
  /\bREEMBOLSAVE\w*\b/,
  /\bNR\b/,
  /\bREFUNDABLE\b/,
  /\bOFERTAS?\b/,
  /\bOFF\b/,
  /\bPROMO\w*\b/,
  /\bAGENCIAS?\b/,
  /\bDISPONIBILIDADE\b/,
  /\bSUJEIT[OA]S?\b/,
  /\bPENDENTES?\b/,
  /\bCONFIRMACAO\b/,
  /\bDIARIAS?\b/,
  /\bPER PAX\b/,
  /\bNET\b/,
  /\bS A\b/,
  /\bLTDA\b/,
  /\bVOLL\b/,
  /\bMIN \d+ NOITES?\b/,
  /\b(?:19|20)\d{2}\b/,
  /\bNULL\b/,
  /\bESPECIA\w*\b/,
  /\bEXTRAS?\b/,
  /\bWITH\b/,
  /\bAND\b/,
  /\bSIZE\b/,
  /\bTAMANHO\b/,
  /\bTYPE\b/,
  /\bCAPACITY\b/,
  /\bCHAIN\b/,
  /\bDISCOUNT\w*\b/,
  /\bLRA\b/,
  /\bOCUPACAO\b/,
  /\bPESSOAS?\b/,
  /\bPERSONAS?\b/,
  /\bFRIGOBAR\b/,
  /\bCORTESIA\b/,
  /\bFOR\b/,
  /\bOR\b/,
  /\bVARIAS?\b/,
  /\bPLUS\b/,
  /\bHOUSE\b/,
  /\bCATEGORIA\b/,
];

/** Regras de cama: [padrão, contagem ('G' = grupo 1 do regex), família].
 *  Ordem importa — explícitas primeiro, convenções depois. */
type BedRule = readonly [RegExp, number | 'G', BedFamily | undefined];
const BED_RULES_EXPLICIT: readonly BedRule[] = [
  [/\b(\d+) CAMAS? (?:DE )?(?:SOLTEIR[OA]S?|INDIVIDUA\w+|SINGLES?)\b/, 'G', 'SINGLE'],
  [/\b(\d+) (?:SINGLE|TWIN) BEDS?\b/, 'G', 'SINGLE'],
  // DOBLES? exige o substantivo CAMA no MESMO padrão (ver comentário em
  // BED_RULES_CONVENTION): com CAMA, "1 cama doble" é tão explícito quanto "1
  // cama de casal".
  [/\b(\d+) CAMAS? (?:DE )?(?:CASAL|DUPLAS?|MATRIMONIA\w+|DOBLES?)\b/, 'G', 'DOUBLE'],
  [/\b(\d+) (?:DOUBLE|QUEEN|KING|COUPLE) BEDS?\b/, 'G', 'DOUBLE'],
  [/\b(\d+) CAMAS? (?:DE )?(?:TAMANHO )?(?:QUEEN|KING)\b/, 'G', 'DOUBLE'],
  [/\b(\d+) CAMAS?\b/, 'G', undefined],
  [/\b(\d+) BEDS?\b/, 'G', undefined],
];
const BED_RULES_CONVENTION: readonly BedRule[] = [
  [/\bSOFAS? ?CAMAS?\b|\bSOFA BED\b|\bSOFA\b/, 1, 'SOFA'],
  [
    /\bCAMAS? (?:DE )?(?:TAMANHO )?(?:QUEEN|KING)(?: SIZE)?\b|\b(?:QUEEN|KING)(?: SIZE)?(?: BED)?\b/,
    1,
    'DOUBLE',
  ],
  [/\bCAMAS? (?:DE )?(?:CASAL|DUPLAS?|MATRIMONIA\w+|DOBLES?)\b/, 1, 'DOUBLE'],
  [/\bCAMAS? (?:DE )?(?:SOLTEIR[OA]S?|INDIVIDUA\w+)\b/, 1, 'SINGLE'],
  [/\bTWINS?\b/, 2, 'SINGLE'],
  [/\bDUPL[OA]S?\b/, 2, 'SINGLE'],
  // "DOBLE" SOLTO (sem CAMA) fica de fora de propósito: em espanhol
  // "habitación doble" é o quarto PARA DUAS PESSOAS, não a cama — a cama real
  // é ambígua (1 casal OU 2 solteiros). Afirmar uma família aqui seria chutar,
  // violando a assimetria dividir>fundir; o resíduo cru é o resultado certo.
  [/\bCASAL\b|\bDOUBLES?\b|\bMATRIMONIA\w+\b|\bCOUPLE\b/, 1, 'DOUBLE'],
  [/\bSOLTEIR[OA]S?\b|\bSINGLES?\b/, 1, 'SINGLE'],
];
const BED_WORD_LEFTOVER = /\bCAMAS?\b|\bBEDS?\b/;
/** Quantificador antes do tipo: "SINGLE DOUBLE BED" = 1 cama de casal. */
const QUANTIFIER_BEFORE_TYPE = /\b(?:SINGLE|1) (DOUBLE|QUEEN|KING|CASAL|MATRIMONIAL)\b/g;

type CapRule = readonly [RegExp, number | 'G' | 'DROP'];
const CAP_RULES: readonly CapRule[] = [
  [/\bOCUPACAO MAX\w* (\d+) PESSOAS?\b/, 'G'],
  [/\b(\d+) PAX\b/, 'G'],
  [/\b(\d+) (?:PESSOAS?|PERSONAS?)\b/, 'G'],
  [/\bOCUPACAO (?:DE )?(\d+)\b/, 'G'],
  [/\bCAPACITY (\d+)\b/, 'G'],
  [/\bOCUPACAO MAX\w*\b/, 'DROP'],
  [/\bTRIPL[OA]S?\b|\bTRIPLES?\b/, 3],
  [/\bQUADRUPL[OA]S?\b|\bQUADRUPLE\b/, 4],
];
/** Ocupação individual: capacidade 1 + cama FRACA (perde de qualquer outra evidência). */
const INDIVIDUAL = /\bINDIVIDUAL\b/;

const BEDROOMS = /\b(\d+) (?:DORMITORIOS?|DORM|BEDROOMS?)\b/;

const STOPWORDS = new Set(
  'DE COM E A O DO DA EM P X N S OU SEM PARA POR NO NA AO AS OS UM UMA THE OF IN C MA CON Y 1 2 3 4 5 6 15'.split(
    ' ',
  ),
);

interface SegmentAxes {
  type?: RoomType;
  tier?: RoomTier;
  tier2?: RoomTier;
  bedrooms?: number;
  capacity?: number;
  explicitBeds: RoomBed[];
  conventionBeds: RoomBed[];
  weakBeds: RoomBed[];
  qualifierCodes: RoomQualifierCode[];
  residue: string[];
  contributed: boolean;
}

function consumeAll(text: string, pattern: RegExp): { text: string; hit: boolean } {
  const global = new RegExp(pattern.source, 'g');
  const hit = global.test(text);
  return { text: hit ? text.replaceAll(global, ' ') : text, hit };
}

function classifySegment(segment: string, learnedNoise?: ReadonlySet<string>): SegmentAxes {
  const axes: SegmentAxes = {
    explicitBeds: [],
    conventionBeds: [],
    weakBeds: [],
    qualifierCodes: [],
    residue: [],
    contributed: false,
  };
  let s = segment;
  // Bedrooms/capacity and INDIVIDUAL run BEFORE noise: NOISE also lists bare
  // words like OCUPACAO/CAPACITY/PESSOAS as fallback cleanup, and stripping
  // them first would eat the multi-word CAP_RULES phrases before they can
  // match (CodeRabbit #2219 on hotel_room_taxonomy.ts).
  s = consumeBedroomsAndCapacity(s, axes);
  s = consumeIndividual(s, axes);
  s = consumeNoise(s);
  s = s.replaceAll(QUANTIFIER_BEFORE_TYPE, '1 $1');
  s = consumeExplicitBeds(s, axes);
  s = consumeConventionBeds(s, axes);
  ({ text: s } = consumeAll(s, BED_WORD_LEFTOVER));
  s = consumeQualifiers(s, axes);
  s = consumeTiers(s, axes);
  s = consumeTypes(s, axes);

  axes.residue = s
    .split(' ')
    .filter((token) => token && !STOPWORDS.has(token) && !learnedNoise?.has(token));
  axes.contributed =
    axes.type !== undefined ||
    axes.tier !== undefined ||
    axes.bedrooms !== undefined ||
    axes.capacity !== undefined ||
    axes.explicitBeds.length > 0 ||
    axes.conventionBeds.length > 0 ||
    axes.weakBeds.length > 0 ||
    axes.qualifierCodes.length > 0;
  return axes;
}

function dedupeBeds(beds: RoomBed[]): RoomBed[] {
  const seen = new Set<string>();
  return beds.filter((bed) => {
    const key = `${String(bed.count)}:${bed.family ?? '?'}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function consumeNoise(s: string): string {
  let text = s;
  for (const pattern of NOISE) {
    ({ text } = consumeAll(text, pattern));
  }
  return text;
}

function consumeBedroomsAndCapacity(s: string, axes: SegmentAxes): string {
  let text = s;
  const bedroomMatch = BEDROOMS.exec(text);
  if (bedroomMatch?.[1]) {
    axes.bedrooms = Number(bedroomMatch[1]);
    text = text.replace(BEDROOMS, ' ');
  }
  for (const [pattern, value] of CAP_RULES) {
    const match = pattern.exec(text);
    if (!match) continue;
    if (value === 'G') {
      axes.capacity ??= Number(match[1]);
      text = text.replace(pattern, ' ');
    } else if (value === 'DROP') {
      text = text.replace(pattern, ' ');
    } else {
      axes.capacity ??= value;
      text = text.replace(pattern, ' ');
    }
  }
  return text;
}

function consumeIndividual(s: string, axes: SegmentAxes): string {
  if (INDIVIDUAL.test(s)) {
    axes.capacity ??= 1;
    axes.weakBeds.push({ count: 1, family: 'SINGLE' });
    return s.replaceAll(new RegExp(INDIVIDUAL.source, 'g'), ' ');
  }
  return s;
}

function consumeExplicitBeds(s: string, axes: SegmentAxes): string {
  let text = s;
  for (const [pattern, count, family] of BED_RULES_EXPLICIT) {
    for (const match of text.matchAll(new RegExp(pattern.source, 'g'))) {
      axes.explicitBeds.push({
        count: count === 'G' ? Number(match[1]) : count,
        ...(family ? { family } : {}),
      });
    }
    ({ text } = consumeAll(text, pattern));
  }
  return text;
}

function consumeConventionBeds(s: string, axes: SegmentAxes): string {
  let text = s;
  for (const [pattern, count, family] of BED_RULES_CONVENTION) {
    if (new RegExp(pattern.source).test(text)) {
      axes.conventionBeds.push({ count: count as number, ...(family ? { family } : {}) });
    }
    ({ text } = consumeAll(text, pattern));
  }
  return text;
}

function consumeQualifiers(s: string, axes: SegmentAxes): string {
  let text = s;
  for (const [pattern, code] of QUALIFIERS) {
    const { text: result, hit } = consumeAll(text, pattern);
    text = result;
    if (hit) axes.qualifierCodes.push(code);
  }
  return text;
}

function consumeTiers(s: string, axes: SegmentAxes): string {
  const tiersFound: RoomTier[] = [];
  let text = s;
  for (const [pattern, tier] of TIERS) {
    const { text: result, hit } = consumeAll(text, pattern);
    text = result;
    if (hit) tiersFound.push(tier);
  }
  tiersFound.sort((a, b) => TIER_RANK[b] - TIER_RANK[a]);
  if (tiersFound[0]) axes.tier = tiersFound[0];
  const loser = tiersFound.find((tier) => tier !== axes.tier);
  if (loser) axes.tier2 = loser;
  return text;
}

function consumeTypes(s: string, axes: SegmentAxes): string {
  let text = consumeAll(s, DEFAULT_TYPE).text;
  for (const [pattern, type] of TYPES) {
    const { text: result, hit } = consumeAll(text, pattern);
    text = result;
    if (hit) axes.type ??= type;
  }
  return text;
}

/** R2 no nome INTEIRO: explícito manda; convenção só completa SOFA (sofá-cama
 *  é uma cama A MAIS de verdade, ex. "1 cama de casal e 1 sofá-cama"). Toda
 *  outra família de convenção quando já há explícito é o MESMO fato
 *  reformulado, não uma 2ª cama: "Quarto Duplo" (convenção: 2×solteiro) + "1
 *  cama de casal" (explícito) é fraseologia de OTA para UM quarto de casal —
 *  DUPLO é a categoria redundante do fornecedor, não uma cama extra, e por
 *  isso é descartado em vez de "completar" a família que falta. Ocupação só
 *  na ausência total. Conflito de famílias (fora SOFA) sem explícito ⇒
 *  recusa. */
function resolveBeds(segments: SegmentAxes[]): { beds: RoomBed[]; ambiguous: boolean } {
  const explicit = dedupeBeds(segments.flatMap((axes) => axes.explicitBeds));
  const convention = dedupeBeds(segments.flatMap((axes) => axes.conventionBeds));
  const weak = dedupeBeds(segments.flatMap((axes) => axes.weakBeds));
  let pool: RoomBed[];
  if (explicit.length > 0) {
    const explicitFamilies = new Set(explicit.map((bed) => bed.family));
    pool = [
      ...explicit,
      ...convention.filter((bed) => bed.family === 'SOFA' && !explicitFamilies.has('SOFA')),
    ];
  } else {
    pool = convention.length > 0 ? convention : weak;
  }
  const conflictFamilies = new Set(
    pool
      .map((bed) => bed.family)
      .filter((family): family is BedFamily => family !== undefined && family !== 'SOFA'),
  );
  if (conflictFamilies.size > 1 && explicit.length === 0) return { beds: [], ambiguous: true };
  return { beds: dedupeBeds(pool), ambiguous: false };
}

export function classifyRoomName(
  raw: string | undefined,
  learnedNoise?: ReadonlySet<string>,
): RoomClassification {
  if (raw === undefined || isPlaceholderRoomName(raw)) return { kind: 'placeholder' };
  const segments = segmentRoomName(raw).map((segment) => classifySegment(segment, learnedNoise));
  // R1: segmento que não contribui eixo é metadado — o resíduo dele NÃO entra.
  const contributing = segments.filter((axes) => axes.contributed);
  if (contributing.length === 0) return { kind: 'illegible' };
  const { beds, ambiguous } = resolveBeds(contributing);
  const tiers = contributing
    .flatMap((axes) => [axes.tier, axes.tier2])
    .filter((tier): tier is RoomTier => tier !== undefined)
    .sort((a, b) => TIER_RANK[b] - TIER_RANK[a]);
  const tier = tiers[0];
  const tier2 = tiers.find((candidate) => candidate !== tier);
  const qualifiers: RoomQualifier[] = [
    ...new Set(contributing.flatMap((axes) => axes.qualifierCodes)),
  ].map((code) => ({ code }));
  for (const token of new Set(contributing.flatMap((axes) => axes.residue))) {
    qualifiers.push({ raw: token });
  }
  const type = contributing.map((axes) => axes.type).find((value) => value !== undefined);
  const bedrooms = contributing.map((axes) => axes.bedrooms).find((value) => value !== undefined);
  const capacity = contributing.map((axes) => axes.capacity).find((value) => value !== undefined);
  const identity: RoomIdentity = {
    ...(type ? { type } : {}),
    ...(tier ? { tier } : {}),
    ...(tier2 ? { tier2 } : {}),
    ...(bedrooms === undefined ? {} : { bedrooms }),
    ...(capacity === undefined ? {} : { capacity }),
    ...(beds.length > 0 ? { beds } : {}),
    ...(qualifiers.length > 0 ? { qualifiers } : {}),
  };
  return { kind: 'classified', identity, ambiguousBeds: ambiguous };
}

/** Comparação ordinal, não `localeCompare`: a chave tem de ser independente de locale
 *  e de versão do ICU. Mesma ordem que o `.sort()` sem comparador já produzia —
 *  comparação de string em JS é ordem de code unit UTF-16. */
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Chave estável de agrupamento: a tupla completa, resíduo incluso. */
export function roomIdentityKey(identity: RoomIdentity): string {
  const beds = [...(identity.beds ?? [])]
    .map((bed) => `${String(bed.count)}x${bed.family ?? '?'}`)
    .sort(byCodeUnit)
    .join('+');
  const qualifiers = [...(identity.qualifiers ?? [])]
    .map((qualifier) => qualifier.code ?? `raw:${qualifier.raw ?? ''}`)
    .sort(byCodeUnit)
    .join('+');
  return [
    identity.type ?? 'QUARTO',
    identity.tier ?? '',
    identity.tier2 ?? '',
    identity.bedrooms ?? '',
    identity.capacity ?? '',
    beds,
    qualifiers,
  ].join('|');
}
